// M004-F0744 / F0746 — a receipt thumbnail from the typed image read with variant=thumb (Spec 1.4.0). Loads when the
// tile scrolls near the viewport, keeps a small module-level cache of object URLs so a filter or a switch between envelopes does not
// refetch or flicker, shows a skeleton while loading and a labelled placeholder for a PDF or an unreadable image. The original is
// never altered.
import React, { useEffect, useRef, useState } from 'react';
import { envelopesApi } from '../../services/api';

const CACHE_LIMIT = 150;
const urlCache = new Map();

const remember = (key, url) => {
  urlCache.set(key, url);
  while (urlCache.size > CACHE_LIMIT) {
    const oldest = urlCache.keys().next().value;
    const stale = urlCache.get(oldest);
    urlCache.delete(oldest);
    if (typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(stale);
  }
};

export const clearThumbCache = () => urlCache.clear();

// `variant: 'thumb'` is the small JPEG the Ledger serves per image receipt (M004-F0746, Spec 1.4.0); a provider that refuses it
// (older than 1.4.0) is asked again for the original, so a tile never stays blank because of the variant.
export const useReceiptImage = (enabled, orgId, envelopeId, docId, variant) => {
  const cacheKey = `${orgId}/${envelopeId}/${docId}/${variant || 'full'}`;
  const [state, setState] = useState(() => ({ src: urlCache.get(cacheKey) || '', failed: false }));
  useEffect(() => {
    if (!enabled) return undefined;
    if (urlCache.has(cacheKey)) {
      setState({ src: urlCache.get(cacheKey), failed: false });
      return undefined;
    }
    let current = true;
    const read = variant
      ? envelopesApi.receiptImage(orgId, envelopeId, docId, variant).catch(() => envelopesApi.receiptImage(orgId, envelopeId, docId))
      : envelopesApi.receiptImage(orgId, envelopeId, docId);
    read
      .then((response) => {
        if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') throw new Error('no object URLs');
        const blob = response?.data instanceof Blob ? response.data : new Blob([response?.data || '']);
        const url = URL.createObjectURL(blob);
        remember(cacheKey, url);
        if (current) setState({ src: url, failed: false });
      })
      .catch(() => { if (current) setState({ src: '', failed: true }); });
    return () => { current = false; };
  }, [enabled, cacheKey, orgId, envelopeId, docId, variant]);
  return state;
};

const useNearViewport = (ref) => {
  const [near, setNear] = useState(typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    if (near || !ref.current) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) { setNear(true); observer.disconnect(); }
    }, { rootMargin: '300px' });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [near, ref]);
  return near;
};

export default function ReceiptThumb({ orgId, envelopeId, receipt, alt = '' }) {
  const ref = useRef(null);
  const near = useNearViewport(ref);
  const isPdf = receipt.content_type === 'application/pdf';
  const { src, failed } = useReceiptImage(near && !isPdf, orgId, envelopeId, receipt.receipt_document_id, 'thumb');
  let body;
  if (isPdf) body = <span className="ew-thumb-file" data-testid={`envelope-receipt-file-${receipt.receipt_document_id}`}>PDF</span>;
  else if (src) body = <img src={src} alt={alt} loading="lazy" draggable={false} />;
  else if (failed) body = <span className="ew-thumb-file ew-thumb-none" data-testid={`envelope-receipt-nopreview-${receipt.receipt_document_id}`}>No preview</span>;
  else body = <span className="ew-skel ew-thumb-skel" aria-hidden="true" />;
  return <span ref={ref} className="ew-thumb">{body}</span>;
}
